import moment from 'moment'
import { useRouter } from 'next/router'
import * as R from 'ramda'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css, ThemeContext } from 'styled-components'

import { Form } from '../../../components/Form'
import { Col, Row } from '../../../components/Layout/FlexGrid'
import NextSeo from '../../../components/NextSeo'
import { btnStyle } from '../../../components/PersonalFundraising/shared'
import SectionTitle from '../../../components/Text/SectionTitle'
import { PAGE } from '../../../constants'
import Button, { NextBtn, PrevBtn } from '../../../containers/Button'
import CreateFormContent from '../../../containers/PersonalFundraising/CreateFormContent'
import { useTracking } from '../../../hooks'
import { useTranslation } from '../../../i18n'
import { setFeedbackText } from '../../../redux/app/actions'
import { getIsUploading } from '../../../redux/file/selectors'
import {
  createFundraisingInitRequestAsync,
  createFundraisingRequestAsync,
  getFundraisingCreatePageRequest,
  patchFundraisingRequestAsync,
  putFundraisingRequestAsync
} from '../../../redux/fundraising/actions'
import { getCreateFormProjectInfo } from '../../../redux/fundraising/selectors'
import { getPageNameFromCurrentPath } from '../../../utils/commonUtils'
import { getColor, getFontSize } from '../../../utils/cssUtils'
import {
  fundraisingCreateFormClickCancelBtn,
  fundraisingCreateFormClickSubmitBtn,
  fundraisingCreateFormView
} from '../../../utils/dataTracker'
import mq from '../../../utils/mediaQuery'
import { emptyStrToNull, isExist } from '../../../utils/ramdaUtils'
import { slugify } from '../../../utils/urlUtils'
import { youtubeIdRegex } from '../../../utils/validator'

const Wrapper = styled.section`
  max-width: 792px;
  width: 100%;
  margin: 0 auto;
  padding: 24px 0;
  ${mq.tablet} {
    padding: 24px 64px;
  }
  ${mq.mobile} {
    padding: 24px 16px;
  }
`
const SubHeadline = styled.h3`
  font-size: ${getFontSize('body2')};
  font-weight: normal;
  color: ${getColor('black')};
  margin-bottom: 16px;
`
const Project = styled.div`
  margin-bottom: 32px;
`
const ProjectName = styled.span`
  display: block;
  font-size: ${getFontSize('body2')};
  font-weight: bold;
  color: ${getColor('black')};
`
const OrgName = styled.span`
  display: flex;
  flex-flow: row nowrap;
  font-size: ${getFontSize('body2')};
  color: ${getColor('black')};
  &::before {
    content: '-';
    display: inline-block;
    margin-right: 8px;
  }
`
const Separator = styled.hr`
  height: 1px;
  border: none;
  background-color: ${getColor('black', 0.2)};
  margin-bottom: 32px;
`

const Placeholder = styled.div`
  height: 56px;
`
const BtnRowWrapper = styled.div`
  ${({ theme }) =>
    theme.isAppview
      ? css`
          height: 56px;
          width: 100%;
          padding: 8px 16px;
          background-color: #fff;
          position: fixed;
          bottom: 0;
          left: 0;
          box-shadow: 0 -2px 23px 0 rgba(0, 0, 0, 0.1);
          z-index: 2;
        `
      : css`
          margin-top: 24px;
        `}
`
const ButtonRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  button {
    &:first-of-type {
      margin-right: auto;
    }
  }
`
const StyledPrevBtn = styled(PrevBtn)`
  ${mq.mobile} {
    min-width: 0;
    flex-shrink: 0;
    padding: 0;
    > div {
      right: 0;
    }
    span {
      display: none;
    }
  }
  ${mq.mobile} {
    width: ${({ isAppview }) => (isAppview ? '38px' : '48px')};
  }
`
const StyledBtn = styled(Button)`
  ${btnStyle}
  margin-right: 24px;
  ${mq.mobile} {
    margin-right: 12px;
  }
  @media (max-width: 360px) {
    margin-right: 4px;
  }
`
const StyledNextBtn = styled(NextBtn)`
  order: 1;
  ${btnStyle}
`

const processSubmittedData = data => {
  const {
    donationItemsDraftSetting,
    donationTarget,
    endDate,
    endTime,
    youtubeId,
    corporateCreatorName,
    corporateCreatorLogoImage,
    corporateCreatorBR,
    titleImage,
    relatedImages = []
  } = data

  const payload = {
    ...R.omit(
      [
        'endDate',
        'contactEmailAgain',
        'corporateCreatorName',
        'corporateCreatorLogoImage',
        'corporateCreatorBR',
        'titleImage',
        'relatedImages'
      ],
      data
    ),
    endTime:
      (endDate && endTime) || endDate
        ? moment(endDate, 'YYYY/MM/DD').add(moment.duration(endTime)).valueOf()
        : null,
    donationTarget: isExist(donationTarget)
      ? Number(donationTarget) * 100
      : null,
    donationItemsDraftSetting: R.map(
      val => (isExist(val) ? Number(val) * 100 : null),
      donationItemsDraftSetting
    ),
    youtubeId:
      R.pipe(R.match(youtubeIdRegex), R.prop(5))(youtubeId) ??
      R.when(R.equals(''), R.always(null))(youtubeId),
    corporateCreator: {
      name: corporateCreatorName,
      logoImageFileId: R.propOr(null, 'fileId', corporateCreatorLogoImage),
      BRFileId: R.propOr(null, 'fileId', corporateCreatorBR)
    },
    titleImageFileId: R.propOr(null, 'fileId', titleImage),
    relatedImageFileIds: R.pluck('fileId')(relatedImages)
  }
  return R.map(emptyStrToNull, payload)
}

const processPopulateData = data => {
  const { donationItemsDraftSetting, donationTarget, endTime, youtubeId } = data

  return {
    ...R.omit(
      ['endTime', 'corporateCreator'],
      R.pick(
        [
          'allowCustomDonation',
          'contactEmail',
          'description',
          'donationItemsDraftSetting',
          'donationTarget',
          'fundraisingEventNo',
          'projectNo',
          'relatedImages',
          'titleImage',
          'title',
          'useCorporateCreator'
        ],
        data
      )
    ),
    ...(isExist(endTime)
      ? {
          endDate: moment(endTime),
          endTime: moment(endTime).format('HH:mm')
        }
      : {}),
    contactEmailAgain: R.prop('contactEmail', data),
    donationTarget: R.unless(R.isNil, R.divide(R.__, 100))(donationTarget),
    donationItemsDraftSetting: R.map(
      R.unless(R.isNil, R.divide(R.__, 100)),
      donationItemsDraftSetting
    ),
    youtubeId: isExist(youtubeId)
      ? 'https://www.youtube.com/watch?v=' + youtubeId
      : null,
    corporateCreatorName: R.path(['corporateCreator', 'name'], data),
    corporateCreatorLogoImage: R.path(['corporateCreator', 'logoImage'], data),
    corporateCreatorBR: R.path(['corporateCreator', 'BR'], data)
  }
}

// TODO: move to config file
const storageKey = process.env.FUNDRAISING_EVENT_FORM_STORAGE_KEY
// fundraisingEventNo === undefined on Create page
export const Create = ({ projectNo, fundraisingEventNo, ...props }) => {
  const router = useRouter()

  const dispatch = useDispatch()

  const track = useTracking()

  const { t } = useTranslation('common')

  const { color } = useContext(ThemeContext)

  const [isPublish, setIsPublish] = useState(false)

  const [shouldHandleOnBeforeUnload, setShouldHandleOnBeforeUnload] = useState(
    true
  )

  const [isSaveDraftLoading, setIsSaveDraftLoading] = useState(false)

  const [isSavePreviewLoading, setIsSavePreviewLoading] = useState(false)

  const fundraisingData = useSelector(
    R.path([
      'fundraisingState',
      'entities',
      'fundraisings',
      fundraisingEventNo
    ]),
    R.equals
  )

  const projectNoFromFundraisingEvent = R.path(
    ['project', 'projectNo'],
    fundraisingData
  )

  projectNo = projectNo || projectNoFromFundraisingEvent

  const [projectName, organizationName, endTime] = useSelector(
    getCreateFormProjectInfo(projectNo),
    R.equals
  )

  const [isAppview, isLoggedIn, isInitialRender, shouldBackHome] = useSelector(
    R.paths([
      ['appState', 'isAppview'],
      ['authState', 'isLoggedIn'],
      ['appState', 'isInitialRender'],
      ['appState', 'shouldBackHome']
    ]),
    R.equals
  )

  const isFileUploading = useSelector(getIsUploading)

  const [formOptions, setFormOptions] = useState({
    defaultValues: {
      allowCustomDonation: true,
      contactEmail: null,
      contactEmailAgain: null,
      description: null,
      donationItemsDraftSetting: [100, 200, 500, 800],
      donationTarget: 1000,
      relatedImages: [],
      title: null,
      titleImage: null,
      youtubeId: null,
      useCorporateCreator: false,
      corporateCreatorName: null,
      corporateCreatorLogoImage: null,
      corporateCreatorBR: null
    }
  })

  const pageName = getPageNameFromCurrentPath(router.pathname)

  const isEdit = pageName === PAGE.FUNDRAISING_DRAFT_EDIT

  const handleBeforeUnload = useCallback(formValues => {
    const data = {
      form: R.omit(['projectNo'], processSubmittedData(formValues)),
      ts: new Date().getTime()
    }
    window.localStorage.setItem(storageKey, JSON.stringify(data))
  }, [])

  const onSubmit = useCallback(
    async (data, e) => {
      const setIsLoading = isPublish
        ? setIsSavePreviewLoading
        : setIsSaveDraftLoading

      const action = isEdit
        ? isPublish
          ? putFundraisingRequestAsync
          : patchFundraisingRequestAsync
        : isPublish
        ? createFundraisingRequestAsync
        : createFundraisingInitRequestAsync

      setShouldHandleOnBeforeUnload(false)

      if (!isLoggedIn) {
        handleBeforeUnload(data)
      }

      try {
        setIsLoading(true)

        const { fundraisingEventNo } = await dispatch(
          action(processSubmittedData(data))
        )

        if (isEdit && !isPublish) {
          setIsLoading(false)

          dispatch(
            setFeedbackText(
              t('personalFundraising.create.message.saveSuccess'),
              'success'
            )
          )
        } else {
          if (!R.isNil(window.localStorage.getItem(storageKey))) {
            window.localStorage.removeItem(storageKey)
          }

          router.replace(
            `/fundraising/${
              isPublish ? 'preview' : 'edit'
            }/[fundraisingEventNo]`,
            `/fundraising/${
              isPublish ? 'preview' : 'edit'
            }/${fundraisingEventNo}`
          )
        }
      } catch (e) {
        setIsLoading(false)

        if (isPublish) {
          dispatch(
            setFeedbackText(
              t('personalFundraising.create.message.errorOccured'),
              'error'
            )
          )
        } else {
          dispatch(
            setFeedbackText(
              t('personalFundraising.create.message.saveFail'),
              'error'
            )
          )
        }

        setShouldHandleOnBeforeUnload(true)
      }
    },
    [isPublish, isEdit, handleBeforeUnload, isLoggedIn]
  )

  const handleBackClick = useCallback(() => {
    if (isInitialRender) {
      if (isEdit) {
        router.push('/account')
      } else {
        router.push(`/project/${projectNo}/${slugify(projectName)}`)
      }
    } else {
      if (!isEdit && shouldBackHome) {
        router.push(`/project/${projectNo}/${slugify(projectName)}`)
      } else if (isEdit) {
        router.push('/account')
      } else {
        router.back()
      }
    }

    track(
      fundraisingCreateFormClickCancelBtn(
        projectNo,
        projectName,
        isEdit ? fundraisingEventNo : undefined,
        isEdit ? 'draft' : undefined
      )
    )
  }, [
    isInitialRender,
    shouldBackHome,
    isEdit,
    router,
    projectNo,
    projectName,
    fundraisingEventNo
  ])

  const handleSubmitBtnClick = e => {
    if (isFileUploading) {
      e.preventDefault()
    }
    track(
      fundraisingCreateFormClickSubmitBtn(
        'preview',
        projectNo,
        projectName,
        isEdit ? fundraisingEventNo : undefined,
        isEdit ? 'draft' : undefined
      )
    )
    setIsPublish(true)
  }

  const handleSaveBtnClick = e => {
    if (isFileUploading) {
      e.preventDefault()
    }

    track(
      fundraisingCreateFormClickSubmitBtn(
        'save_draft',
        projectNo,
        projectName,
        isEdit ? fundraisingEventNo : undefined,
        isEdit ? 'draft' : undefined
      )
    )
    setIsPublish(false)
  }

  useEffect(() => {
    let storedForm = window.localStorage.getItem(storageKey)

    if (!R.isNil(storedForm)) {
      storedForm = JSON.parse(storedForm)
      if (
        Math.floor((new Date().getTime() - storedForm.ts) / 1000) >
        60 * 60 * 24
      ) {
        storedForm = null
        window.localStorage.removeItem(storageKey)
      }
    }

    if (!R.isNil(storedForm) && !isEdit) {
      setFormOptions({ defaultValues: processPopulateData(storedForm.form) })
    }
  }, [isEdit])

  useEffect(() => {
    if (isEdit) {
      setFormOptions({ defaultValues: processPopulateData(fundraisingData) })
    }
  }, [isEdit, fundraisingData])

  useEffect(() => {
    track(fundraisingCreateFormView(projectNo, projectName), true)
  }, [])

  return (
    <Wrapper>
      <NextSeo title={t('seo.personalFundraising.create.title')} />
      <SectionTitle color={color.black}>
        {t('personalFundraising.create.headline')}
      </SectionTitle>
      <Project>
        <SubHeadline>
          {t('personalFundraising.create.youHaveChosen')}
        </SubHeadline>
        <ProjectName>{projectName}</ProjectName>
        <OrgName>{organizationName}</OrgName>
      </Project>
      <Separator />
      <SubHeadline>
        {t('personalFundraising.create.fillInfoForYourPage')}
      </SubHeadline>
      <Form options={formOptions} onSubmit={onSubmit} noValidate>
        <CreateFormContent
          defaultValues={formOptions.defaultValues}
          projectNo={projectNo}
          projectName={projectName}
          fundraisingEventNo={fundraisingEventNo}
          isEdit={isEdit}
          setIsPublish={setIsPublish}
          isSavePreviewLoading={isSavePreviewLoading}
          isSaveDraftLoading={isSaveDraftLoading}
          endTime={endTime}
          isPublish={isPublish}
          onBeforeUnload={isEdit ? undefined : handleBeforeUnload}
          shouldHandleOnBeforeUnload={!isEdit && shouldHandleOnBeforeUnload}
        />
        <BtnRowWrapper>
          <Row gutter={[24, 48]}>
            <Col>
              <ButtonRow>
                <StyledPrevBtn
                  isAppview={isAppview}
                  type='button'
                  disabled={isFileUploading}
                  onClick={handleBackClick}
                >
                  <span>{t('personalFundraising.create.button.back')}</span>
                </StyledPrevBtn>
                <StyledNextBtn
                  type='submit'
                  onClick={handleSubmitBtnClick}
                  loading={isSavePreviewLoading}
                  disabled={isFileUploading}
                >
                  {t('personalFundraising.create.button.saveAndPreview')}
                </StyledNextBtn>
                <StyledBtn
                  height={isAppview ? 38 : 48}
                  transparent
                  type='submit'
                  onClick={handleSaveBtnClick}
                  loading={isSaveDraftLoading}
                  disabled={isFileUploading}
                >
                  {t('personalFundraising.create.button.saveDraft')}
                </StyledBtn>
              </ButtonRow>
            </Col>
          </Row>
        </BtnRowWrapper>
        {isAppview && <Placeholder />}
      </Form>
    </Wrapper>
  )
}

export default Create

Create.getInitialProps = async ({ res, query, store }) => {
  const { projectNo } = query
  try {
    await store.nextDispatch(getFundraisingCreatePageRequest(projectNo))
  } catch (e) {
    res.redirect('/')
    console.log(e)
  }
  return { projectNo }
}
